/**
 * Preview-mode cookie SHARED helpers — safe to import from any file that
 * compiles into the Angular client bundle. Contains ONLY pure string/parsing
 * code. NO Node built-ins, NO crypto.
 *
 * The matching server-only module is `./preview-cookie-server.ts`.
 */

export const PREVIEW_COOKIE_NAME = '__samotics_preview';

/**
 * 8 hours — matches a typical editor workday so the cookie doesn't linger
 * on shared machines indefinitely. Referenced by both server (cookie mint)
 * and shared (verifier max-age check) code.
 */
export const PREVIEW_COOKIE_MAX_AGE_SECONDS = 8 * 60 * 60;

/**
 * Parse a raw Cookie header (`a=1; b=2`) and return the preview cookie
 * value, or undefined when absent. Vanilla implementation avoids pulling
 * in the `cookie` npm package for the single cookie we care about.
 *
 * This function is pure and safe for the client bundle — it only does
 * string splitting and decodeURIComponent.
 */
export function readPreviewCookie(cookieHeader: string | undefined | null): string | undefined {
  if (!cookieHeader) return undefined;
  for (const pair of cookieHeader.split(';')) {
    const [rawK, ...rest] = pair.split('=');
    if (!rawK) continue;
    const k = rawK.trim();
    if (k !== PREVIEW_COOKIE_NAME) continue;
    return decodeURIComponent(rest.join('=').trim());
  }
  return undefined;
}

/**
 * Split a cookie value into `{ ts, sig }`. Returns null when malformed.
 * Shared because the server verifier uses it too; also unit-testable without
 * needing Node's crypto.
 */
export function parsePreviewCookieValue(value: string | undefined): { ts: string; sig: string } | null {
  if (!value) return null;
  const dot = value.indexOf('.');
  if (dot <= 0) return null;
  const ts = value.slice(0, dot);
  const sig = value.slice(dot + 1);
  if (!/^\d+$/.test(ts)) return null;
  if (!/^[0-9a-f]+$/i.test(sig)) return null;
  return { ts, sig };
}

/**
 * Check the timestamp-age component only (no signature verification).
 * Exported so the interceptor can short-circuit expired cookies WITHOUT
 * needing to call into the Node-only HMAC verifier. Full security still
 * requires verifyPreviewCookie() from preview-cookie-server.ts.
 */
export function isCookieTimestampFresh(ts: string, nowMs: number = Date.now()): boolean {
  const ageMs = nowMs - Number(ts);
  if (ageMs < 0) return false; // clock skew / future-dated token
  return ageMs <= PREVIEW_COOKIE_MAX_AGE_SECONDS * 1000;
}
